import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AlertCircle } from "lucide-react";
import AuthPageShell from "../components/auth/AuthPageShell";
import {
  AuthTitle,
  AuthSub,
  Divider,
  Label,
  Input,
  Field,
  Submit,
  Err,
  Ok,
  Footer,
} from "../components/auth/authFormStyles";
import { cPath } from "../constants";
import * as authServices from "../services/authServices";
import useSEO from "../hooks/useSEO";

const Back = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.primary});
  &:hover {
    text-decoration: underline;
  }
`;

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authServices.requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const seo = useSEO({ title: "Forgot Password", noindex: true, nofollow: true });

  return (
    <>
      {seo}
      <AuthPageShell heroHint="We’ll email you a link to choose a new password.">
      <Divider />
      <AuthTitle>Reset password</AuthTitle>
      <AuthSub>
        {sent
          ? process.env.NODE_ENV === "development"
            ? t("auth.forgotPassword.sentDev")
            : t("auth.forgotPassword.sent")
          : t("auth.forgotPassword.prompt")}
      </AuthSub>
      {error ? (
        <Err role="alert">
          <AlertCircle size={18} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{error}</span>
        </Err>
      ) : null}
      {!sent ? (
        <form onSubmit={submit}>
          <Field>
            <Label htmlFor="fp-email">Email</Label>
            <Input
              id="fp-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </Field>
          <Submit type="submit" disabled={loading}>
            {loading ? "Sending…" : "Send reset link"}
          </Submit>
        </form>
      ) : (
        <Ok>Done. Check your inbox or server logs, then open the reset link.</Ok>
      )}
      <Footer>
        <Back to={cPath("signin")}>← Back to sign in</Back>
      </Footer>
      </AuthPageShell>
    </>
  );
}
